import React, { useState, useEffect, useMemo, useRef } from "react";
import AudioPlayIcon from "@/assets/summary/ic_audio_play.svg?react";
import AudioPauseIcon from "@/assets/summary/ic_audio_pause.svg?react";
import Back5Icon from "@/assets/summary/ic_back5.svg?react";
import Forward5Icon from "@/assets/summary/Ic_forward5.svg?react";
import UnfoldIcon from "@/assets/summary/unfold.svg";
import FoldIcon from "@/assets/summary/fold.svg";
import MoreIcon from "@/assets/summary/more.svg?react";
import { formatRecordDuration } from "@/utils/formatter.ts";
import PlayRateModal from "../RateModal";
import MorePanel from "../MorePanel";
import AudioPlayerIns from "@/component/AudioPlayer/AudioPlayerIns";
import { IAudioInfo } from "@/types/summary";
import "./index.css";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { message } from "antd";
import Tooltip from "@/component/Tooltip";
import fridayReporter from "@/utils/friday";
import VideoEnter from "../VideoEnter";
import { debounce } from "@common/utils";
import AVPlayerHelper from "@/bussiness/AVPlayerHelper";
import IcVideoLoading from "@/assets/app/app_loading.svg?react";
import IcVideoError from "@/assets/app/app_error.svg?react";
import { getTotalDiffSeconds } from "@/utils/getTotalDiffSeconds";
import summaryCommonStore from "@/store/summaryCommon.store";

interface IProps {
  audioInfo?: IAudioInfo | null;
  videoLoadFailure: boolean;
  scTimeList?: number[]; //[语句1开始，语句1结束，语句2开始，语句2结束，语句3开始，语句3结束]
  onCurrentNumChange?: (currentNum: number) => void;
  onAudioInsLoaded?: (audioIns: AudioPlayerIns) => void;
  onModifyPlayingTime?: (time: number) => void;
  onIsPlayingChange?: (isPlaying: boolean) => void;
}

const AudioPlayer: React.FC<IProps> = (props) => {
  const propsRef = useRef(props);
  propsRef.current = props;
  const { audioInfo, scTimeList, videoLoadFailure } = props;
  // const [myAudio, setMyAudio] = useState<AudioPlayerIns | null>(null);
  const myAudio = useRef<AudioPlayerIns | null>(null);
  const [isPlaying, setPlaying] = useState<boolean>(false);
  const [currentNum, setCurrentNum] = useState<number>(0);
  const [durationNum, setDurationNum] = useState<number>(0);
  const [playRate, setPlayRate] = useState("1.0");
  const [showRateModal, setShowRateModal] = useState(false);
  const [skipBlank, setSkipBlank] = useState(false);
  const [showMorePanel, setShowMorePanel] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const isSliderChanging = useRef(false);

  const totalSkipTime = useMemo(() => {
    return getTotalDiffSeconds(durationNum, scTimeList || []);
  }, [scTimeList, durationNum]);

  // 退出页面时，暂停播放音频
  useEffect(() => {
    return () => {
      console.log("audioPlayer unmount");
      myAudio.current && myAudio.current.pause();
      AVPlayerHelper.pauseAudio();
    };
  }, [audioInfo]);

  useEffect(() => {
    const {
      onCurrentNumChange,
      onModifyPlayingTime,
      onAudioInsLoaded,
      onIsPlayingChange,
    } = propsRef.current;
    if (!audioInfo) return;
    console.log("audioInfo", audioInfo);
    const { audioDuration, audioSrc } = audioInfo;
    let tempAudio: AudioPlayerIns | null = new AudioPlayerIns({
      audioDuration,
      audioSrc,
      onIsPlayingChange: (isPlaying: boolean) => {
        setPlaying(isPlaying);
        onIsPlayingChange?.(isPlaying);
      },
      onCurrentNumChange: (currentNum: number) => {
        setCurrentNum(currentNum);
        onCurrentNumChange && onCurrentNumChange(currentNum);
      },
      onDurationNumChange: (durationNum: number) => {
        setDurationNum(durationNum);
      },
      onModifyPlayingTime,
    });
    myAudio.current = tempAudio;
    onAudioInsLoaded && onAudioInsLoaded(tempAudio);

    return () => {
      tempAudio?.destroy();
      tempAudio = null;
    };
  }, [audioInfo]);

  useEffect(() => {
    if (!myAudio || !scTimeList) return;
    myAudio.current?.setScTimeList(scTimeList);
    myAudio.current?.pause
  }, [scTimeList, myAudio]);

  // 播放暂停
  const toggle = () => {
    if (!myAudio.current) return;
    myAudio.current.toggle();
    if (!isPlaying) {
      fridayReporter.pushData({
        eventCode: "SummaryPage_Play_PlayAudio",
      });
    }
  };

  useEffect(() => {
    if (isSliderChanging.current) {
      return;
    }
    setSliderValue(durationNum ? (currentNum / durationNum) * 100 : 0);
  }, [durationNum, currentNum, isSliderChanging]);

  // 滑块拖动快进，快退
  const changeValue = (val: number | number[]) => {
    isSliderChanging.current = false;
    handleSliderChange(val, true);
  };
  // 滑块拖动过程，更新进度条
  const changingValue = (val: number | number[]) => {
    if (!audioInfo) return;
    isSliderChanging.current = true;
    handleSliderChange(val);
  };

  const handleSliderChange = (val: number | number[], seek?: boolean) => {
    if (!audioInfo) return;
    if (typeof val !== "number") return;
    setSliderValue(val);
    const time = (val / 100) * durationNum;
    if (myAudio.current && seek) {
      myAudio.current.setCurrentTime(time);
      AVPlayerHelper.changeSliderValueCb();
      fridayReporter.pushData({
        eventCode: "SummaryPage_Play_ClickControlBar",
      });
    }
  };

  const handleChangePlayRate = (playRate: string) => {
    if (!myAudio) return;
    myAudio.current?.setPlayRate(Number(playRate));
    setPlayRate(playRate);
  };

  const handleChangeSkipBlank = (skipBlank: boolean) => {
    if (!myAudio) return;
    myAudio.current?.setSkipBlank(skipBlank);
    setSkipBlank(skipBlank);
    // 弹toast 提示
    if (skipBlank) {
      message.success(`已跳过${totalSkipTime}`);
    }
  };

  const handleBack5s = debounce(() => {
    if (!myAudio.current) return;
    myAudio.current.setCurrentTime(currentNum - 5);
    AVPlayerHelper.changeSliderValueCb();
    fridayReporter.pushData({
      eventCode: "SummaryPage_Play_Retreat5s",
    });
  });


  const handleForward5s = debounce(() => {
    if (!myAudio.current) return;
    myAudio.current.setCurrentTime(currentNum + 5);
    AVPlayerHelper.changeSliderValueCb();
    fridayReporter.pushData({
      eventCode: "SummaryPage_Play_Advance5s",
    });
  });

  const cursorCss = audioInfo ? "cursor-pointer" : "cursor-default";

  return (
    <>
      <div className={`h-17.5 rounded-4 relative flex items-center px-4 justify-between`}>
        <div className="flex text-3.5 items-center">
          {(audioInfo?.recordStatus === true && !audioInfo?.videoSrc && !videoLoadFailure) && (
            <Tooltip hover content={"上传中"}>
              <div className="bg-video-loading w-18 h-10 mr-3 rd-1 flex items-center justify-center">
                <IcVideoLoading className="rotation size-4" />
              </div>
            </Tooltip>
          )}
          {(audioInfo?.recordStatus === true && videoLoadFailure) && (
            <Tooltip hover content={"网络异常，点击刷新"}>
              <div className="bg-#0000000D w-18 h-10 mr-3 rd-1 flex items-center justify-center"
                onClick={() => {
                  summaryCommonStore.getState().refreshMediaInfo();
                }}>
                <IcVideoError className="size-4" />
              </div>
            </Tooltip>
          )}
          {audioInfo?.videoSrc &&
            <VideoEnter
              openCb={() => { myAudio.current?.pause(); }}
              isPlaying={isPlaying} />
          }
          <span className="color-#191D26">
            {formatRecordDuration(
              (sliderValue / 100) * durationNum,
              durationNum
            )}
          </span>
          <span className="color-#626773">
            /{formatRecordDuration(durationNum, durationNum)}
          </span>
        </div>
        <div className="flex">
          {
            <div className="text-3 text-mx-tertiary flex items-center rounded-25 pl-4 pr-2">
              <PlayRateModal
                visible={showRateModal && !!audioInfo}
                setVisible={setShowRateModal}
                playRate={playRate}
                setPlayRate={handleChangePlayRate}
                mode={"light"}>
                <div
                  className={`flex items-center border-1 border-solid border-#ECEEF3 rounded-25 p-y-2 pl-4 pr-2 ${cursorCss}`}
                >
                  {`${playRate}X`}
                  {showRateModal ? (
                    <img src={FoldIcon} className="size-4 ml-1" />
                  ) : (
                    <img src={UnfoldIcon} className="size-4 h-4 ml-1" />
                  )}
                </div>
              </PlayRateModal>
            </div>
          }
          <div className="ml-4 size-8 flex items-center justify-center">
            <MorePanel
              visible={showMorePanel && !!audioInfo}
              setVisible={setShowMorePanel}
              skipBlank={skipBlank}
              setSkipBlank={handleChangeSkipBlank}
            >
              <MoreIcon className={`size-4 relative top-.5 ${cursorCss}`} />
            </MorePanel>
          </div>
        </div>
        <div className="absolute left-1/2 -translate-x-50% flex items-center">
          <Tooltip content="后退5秒（←）" hover={true}>
            <Back5Icon
              className={`size-6 ${cursorCss} tap-highlight-transparent`}
              onClick={handleBack5s}
            />
          </Tooltip>
          {isPlaying ? (
            <Tooltip content="暂停" hover={true}>
              <AudioPauseIcon
                className={`size-10 mx-6 ${cursorCss} tap-highlight-transparent`}
                onClick={toggle}
              />
            </Tooltip>
          ) : (
            <Tooltip content="播放" hover={true}>
              <AudioPlayIcon
                className={`size-10 mx-6 ${cursorCss} tap-highlight-transparent`}
                onClick={toggle}
              />
            </Tooltip>
          )}
          <Tooltip content="前进5秒（→）" hover={true}>
            <Forward5Icon
              className={`size-6 ${cursorCss} tap-highlight-transparent`}
              onClick={handleForward5s}
            />
          </Tooltip>
        </div>
        <div className="absolute top--1.5 left-0 right-0 min-w-full overflow-hidden flex flex-col text-3 text-#666 font-400">
          <Slider
            step={0.01}
            value={sliderValue}
            className={`audio-slider ${!audioInfo && "slider-disabled"}`}
            onChangeComplete={changeValue}
            onChange={changingValue}
          />
        </div>
      </div>
    </>
  );
};

export default AudioPlayer;
